/**
 * Cookie consent model — the single source of truth for the self-hosted
 * consent layer that replaced CookieYes.
 *
 * Four things need to agree on the same country list and the same category
 * semantics, so they all import from here:
 *   - the inline snippet injected into index.html (see client/vite.config.ts),
 *     which sets Google Consent Mode v2 defaults before GTM loads
 *   - the client consent module (client/src/lib/consent/)
 *   - the server, when it decides whether a tracking event may be forwarded to
 *     Meta / GA4 on behalf of a client that sent no explicit consent
 *   - the consent audit log
 *
 * Keeping the list in one place is the whole point: if the HTML snippet says
 * "default denied in the EEA" and the banner logic disagrees about which
 * countries those are, users get tracked in a region where they shouldn't be,
 * silently, and nothing in the app looks broken.
 */

// ============================================================================
// CATEGORIES
// ============================================================================

/**
 * `necessary` is always granted and is not user-toggleable: session cookies,
 * the Firebase auth token, the Stripe fraud cookie, and the consent cookie
 * itself. The other three map onto Google Consent Mode signals below.
 */
export const CONSENT_CATEGORIES = [
  "necessary",
  "functional",
  "analytics",
  "marketing",
] as const;

export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

export type OptionalConsentCategory = Exclude<ConsentCategory, "necessary">;

/** The user-toggleable subset, in the order the preference centre renders them. */
export const OPTIONAL_CONSENT_CATEGORIES: readonly OptionalConsentCategory[] = [
  "functional",
  "analytics",
  "marketing",
];

export type ConsentState = Record<ConsentCategory, boolean>;

/**
 * Bumping this re-prompts everyone. Do it when a new vendor is added to a
 * category, or when the categories themselves change meaning — not for copy
 * edits, which would burn consent rates for nothing.
 */
export const CONSENT_POLICY_VERSION = 1;

/**
 * First-party cookie. Written on the registrable domain (`.outclose.com`) so a
 * choice made on the marketing site carries into app.outclose.com, matching
 * what `cross_subdomain_cookie` already does for the PostHog distinct_id.
 */
export const CONSENT_COOKIE_NAME = "oc_consent";

/** One year, the conventional ceiling under the EDPB's guidance. */
export const CONSENT_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

export function allDenied(): ConsentState {
  return { necessary: true, functional: false, analytics: false, marketing: false };
}

export function allGranted(): ConsentState {
  return { necessary: true, functional: true, analytics: true, marketing: true };
}

// ============================================================================
// REGIONS
// ============================================================================

/**
 * Where prior opt-in is required before non-essential storage: the EEA (EU 27
 * plus Iceland, Liechtenstein, Norway), the UK, and Switzerland.
 *
 * ISO 3166-1 alpha-2, uppercase. These same codes are valid ISO 3166-2 region
 * values for `gtag('consent', 'default', { region: [...] })`, which is why the
 * list can be shared verbatim with the HTML snippet.
 */
export const OPT_IN_COUNTRIES: readonly string[] = [
  // EU 27
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
  "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL",
  "PL", "PT", "RO", "SK", "SI", "ES", "SE",
  // EEA non-EU
  "IS", "LI", "NO",
  // UK (UK GDPR + PECR) and Switzerland (revFADP)
  "GB", "CH",
] as const;

/**
 * `opt-in`  — nothing non-essential runs until the user actively agrees.
 * `opt-out` — non-essential storage is on by default; the user can withdraw it
 *             at any time through the same preference centre.
 *
 * US state laws (CPRA and friends) are opt-out regimes, so they fall in the
 * second bucket. If we ever need a genuine "Do Not Sell or Share" link for
 * California specifically, that is a third tier keyed on `region`, not a change
 * to this one.
 */
export type ConsentRegime = "opt-in" | "opt-out";

export function regimeForCountry(country: string | null | undefined): ConsentRegime {
  if (!country) return "opt-in"; // unknown location is treated as the strict case
  return OPT_IN_COUNTRIES.includes(country.toUpperCase()) ? "opt-in" : "opt-out";
}

/** What applies before the user has expressed any choice. */
export function defaultConsentForRegime(regime: ConsentRegime): ConsentState {
  return regime === "opt-in" ? allDenied() : allGranted();
}

/**
 * The state to apply when nothing has been chosen yet, accounting for a browser
 * that has already answered on the user's behalf.
 *
 * Global Privacy Control is a binding opt-out under the California, Colorado
 * and Connecticut statutes rather than an advisory hint, so where the regime
 * default would be a grant, GPC withdraws the marketing half of it. It does not
 * touch analytics: GPC signals an objection to sale and sharing for targeted
 * advertising, not to measurement.
 *
 * In an opt-in region GPC changes nothing, because nothing was going to be
 * granted anyway.
 */
export function impliedConsentState(
  regime: ConsentRegime,
  gpcEnabled: boolean
): ConsentState {
  if (regime === "opt-in") return allDenied();
  return gpcEnabled ? { ...allGranted(), marketing: false } : allGranted();
}

// ============================================================================
// GOOGLE CONSENT MODE v2
// ============================================================================

export type GoogleConsentSignal = "granted" | "denied";

export type GoogleConsentPayload = {
  ad_storage: GoogleConsentSignal;
  ad_user_data: GoogleConsentSignal;
  ad_personalization: GoogleConsentSignal;
  analytics_storage: GoogleConsentSignal;
  functionality_storage: GoogleConsentSignal;
  personalization_storage: GoogleConsentSignal;
  security_storage: GoogleConsentSignal;
};

/**
 * Our four categories translated into the seven signals Google tags read.
 *
 * `security_storage` is always granted (fraud prevention, strictly necessary).
 * `functionality_storage` follows the functional category — language choice and
 * theme live there. The three ad_* signals are what Consent Mode v2 added and
 * what Google Ads / Meta audience building actually depend on, so they all
 * follow `marketing` rather than being lumped in with analytics.
 */
export function googleConsentPayload(state: ConsentState): GoogleConsentPayload {
  const marketing: GoogleConsentSignal = state.marketing ? "granted" : "denied";
  return {
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    analytics_storage: state.analytics ? "granted" : "denied",
    functionality_storage: state.functional ? "granted" : "denied",
    personalization_storage: state.functional ? "granted" : "denied",
    security_storage: "granted",
  };
}

/**
 * The dataLayer event pushed alongside every consent command.
 *
 * Google's own tags read the `gtag('consent', …)` command directly, but
 * third-party tags in the container (Meta, TikTok) do not understand Consent
 * Mode at all. They are gated in GTM on this event and its variables instead.
 * See docs/CONSENT.md for the container-side setup.
 */
export const CONSENT_DATALAYER_EVENT = "cookie_consent_update";

export type ConsentDataLayerPayload = {
  event: typeof CONSENT_DATALAYER_EVENT;
  consent_functional: boolean;
  consent_analytics: boolean;
  consent_marketing: boolean;
  consent_regime: ConsentRegime;
  consent_country: string | null;
  /** 'stored' on page load from an existing choice, 'user' on an active change. */
  consent_source: "stored" | "user" | "implied";
};

// ============================================================================
// COOKIE ENCODING
// ============================================================================

/**
 * How the stored state came about.
 *
 * `user` is an answer someone actually gave. `implied` is the regime default
 * applied in an opt-out region, persisted only so the next page load does not
 * have to re-derive it.
 *
 * The distinction is load-bearing, not bookkeeping. An implied grant is lawful
 * exactly where it was made: carry it into a region that requires prior opt-in
 * and it becomes tracking without consent. Someone who first visits from the US
 * and next visits from Berlin must be asked, and `consentNeedsPrompt` is what
 * makes that happen.
 */
export type ConsentSource = "user" | "implied";

export type StoredConsent = {
  /** Policy version this choice was given under. */
  v: number;
  /** Granted categories. `necessary` is implied and not stored. */
  c: { functional: boolean; analytics: boolean; marketing: boolean };
  /** Unix seconds. */
  t: number;
  /** Regime in force when the choice was made, for the audit trail. */
  r: ConsentRegime;
  /** Absent on cookies written before this field existed; treated as `user`. */
  s?: ConsentSource;
};

export function storedConsentSource(stored: StoredConsent): ConsentSource {
  return stored.s === "implied" ? "implied" : "user";
}

/**
 * Whether the banner must be shown despite a stored state.
 *
 * True only for the case that matters: a grant nobody actually gave, being
 * presented to someone whose location requires that they be asked.
 */
export function consentNeedsPrompt(
  stored: StoredConsent | null,
  regime: ConsentRegime
): boolean {
  if (!stored) return regime === "opt-in";
  return regime === "opt-in" && storedConsentSource(stored) === "implied";
}

export function encodeConsentCookie(
  state: ConsentState,
  regime: ConsentRegime,
  source: ConsentSource
): string {
  const stored: StoredConsent = {
    v: CONSENT_POLICY_VERSION,
    c: {
      functional: state.functional,
      analytics: state.analytics,
      marketing: state.marketing,
    },
    t: Math.floor(Date.now() / 1000),
    r: regime,
    s: source,
  };
  return encodeURIComponent(JSON.stringify(stored));
}

/**
 * Returns null for anything unparseable or written under an older policy
 * version, which the callers treat as "no choice yet" and re-prompt.
 *
 * The inline HTML snippet reimplements this in three lines of ES5 because it
 * has to run before any bundle loads. Keep the two in step: the format is
 * deliberately trivial so that stays cheap.
 */
export function decodeConsentCookie(raw: string | null | undefined): StoredConsent | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as StoredConsent;
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.v !== CONSENT_POLICY_VERSION) return null;
    if (!parsed.c || typeof parsed.c !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function storedToConsentState(stored: StoredConsent): ConsentState {
  return {
    necessary: true,
    functional: !!stored.c.functional,
    analytics: !!stored.c.analytics,
    marketing: !!stored.c.marketing,
  };
}
